import React, { useState, useEffect, useRef } from "react";
import "./Checkout.css";
import { useNavigate } from "react-router-dom";
import {
  FaShieldAlt, FaTag, FaTruck, FaCheckCircle,
  FaTrash, FaMapMarkerAlt, FaLock, FaEdit
} from "react-icons/fa";

const API        = process.env.REACT_APP_API_URL || "http://localhost:8000";
const FREE_ABOVE = 999;

// ══════════════════════════════════════════════════════
//  ✅ NEW — PINCODE → STATE DETECTION
//  India Post pincode prefix ranges mapped to states, so
//  shipping is computed from the pincode the customer types,
//  not just whatever they happen to type in the free-text
//  State field (which can be misspelled/wrong).
// ══════════════════════════════════════════════════════
const PINCODE_STATE_MAP = [
  { range: [110000, 110099], state: "Delhi" },
  { range: [121000, 136999], state: "Haryana" },
  { range: [140000, 160999], state: "Punjab" },
  { range: [171000, 177999], state: "Himachal Pradesh" },
  { range: [180000, 194999], state: "Jammu and Kashmir" },
  { range: [200000, 285999], state: "Uttar Pradesh" },
  { range: [246000, 263999], state: "Uttarakhand" },
  { range: [301000, 345999], state: "Rajasthan" },
  { range: [360000, 396999], state: "Gujarat" },
  { range: [400000, 445999], state: "Maharashtra" },
  { range: [450000, 488999], state: "Madhya Pradesh" },
  { range: [490000, 497999], state: "Chhattisgarh" },
  { range: [500000, 509999], state: "Telangana" },
  { range: [510000, 535999], state: "Andhra Pradesh" },
  { range: [516000, 524999], state: "Andhra Pradesh" },
  { range: [560000, 591999], state: "Karnataka" },
  { range: [600000, 643999], state: "Tamil Nadu" },
  { range: [625000, 643999], state: "Tamil Nadu" },
  { range: [670000, 695999], state: "Kerala" },
  { range: [700000, 743999], state: "West Bengal" },
  { range: [751000, 770999], state: "Odisha" },
  { range: [781000, 788999], state: "Assam" },
  { range: [790000, 792999], state: "Arunachal Pradesh" },
  { range: [793000, 794999], state: "Meghalaya" },
  { range: [795000, 795999], state: "Manipur" },
  { range: [796000, 796999], state: "Mizoram" },
  { range: [797000, 798999], state: "Nagaland" },
  { range: [799000, 799999], state: "Tripura" },
  { range: [800000, 855999], state: "Bihar" },
  { range: [814000, 835999], state: "Jharkhand" },
];

const detectStateFromPincode = (pincode) => {
  const pin = parseInt(pincode, 10);
  if (isNaN(pin) || String(pincode).length !== 6) return null;
  // Sort by range start descending so overlapping/more-specific ranges win
  const sorted = [...PINCODE_STATE_MAP].sort((a, b) => b.range[0] - a.range[0]);
  for (const { range, state } of sorted) {
    if (pin >= range[0] && pin <= range[1]) return state;
  }
  return null;
};

// ✅ NEW — full dropdown list, replaces the free-text State input
const INDIA_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu",
  "Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu","Delhi",
  "Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

// ✅ KEY: Get per-size stock — never use item.stock directly
function getStockForSize(item, size) {
  if (!size) return Number(item.stock) || 0;

  // Priority 1: sizeStock map (per size)
  if (item.sizeStock) {
    let map = item.sizeStock;
    if (map instanceof Map) {
      map = Object.fromEntries(map);
    }
    if (map && typeof map === "object" && !Array.isArray(map)) {
      if (map[size] !== undefined) return Number(map[size]) || 0;
    }
  }

  // Priority 2: Even-split estimate if product has sizes listed but no sizeStock
  if (item.sizes && item.sizes.length > 0) {
    const sizes = item.sizes;
    const stock = Number(item.stock) || 0;
    const idx = sizes.indexOf(size);
    if (idx !== -1) {
      const base = Math.floor(stock / sizes.length);
      const extra = stock % sizes.length;
      return base + (idx < extra ? 1 : 0);
    }
  }

  // Priority 3: Fallback for products without sizes
  return Number(item.stock) || 0;
}

// ══════════════════════════════════════════════════════
//  ✅ UNIFIED SHIPPING LOGIC
//  • Andhra Pradesh & Telangana → base ₹80
//  • All other states           → base ₹100
//  • Blouses: base covers first 5, +₹15 for every blouse beyond that
//  • Sarees:  base covers only the 1st saree, then +half the base
//    rate for every saree after that:
//      AP/TS  → 1: ₹80,  2: ₹120,  3: ₹160   (+₹40 per extra saree)
//      Others → 1: ₹100, 2: ₹150,  3: ₹200   (+₹50 per extra saree)
//  • Everything else: flat base rate
//  • Mixed cart: highest category charge wins (matches existing pattern)
//  • ✅ COMBO (sarees / mul cotton, with or without blouses, nothing else):
//      1 saree  → ₹80 AP & Telangana / ₹100 other states, 1 blouse FREE
//      each extra blouse → +₹5      each extra saree → +₹5
//  • ✅ BLOUSES ONLY (pan-India): up to 4 blouses ₹79, each extra +₹5
// ══════════════════════════════════════════════════════
const AP_TS_STATES = ["Andhra Pradesh", "Telangana"];
const getStateShippingRate = (stateName) => {
  if (!stateName) return 100;
  const normalized = stateName.trim().toLowerCase();
  const isAPTS = AP_TS_STATES.some(s => s.toLowerCase() === normalized);
  return isAPTS ? 80 : 100;
};

// Blouse styles are now categories of their own ("Round Neck – Front Hook",
// "Full Sleeves – Without Hook", "DTF Print – …") that don't contain the
// word "blouse", so match those names too.
const isBlouse = (item) =>
  /blouse|sleeve|neck|dtf print/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const isSaree = (item) =>
  /saree|sari/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
// "Mul Cotton" is the saree line — counted with the sarees for combo shipping
const isMulCotton = (item) =>
  /mul\s*cotton/i.test(`${item.name} ${item.category || ""} ${item.fabric || ""}`);
const COMBO_EXTRA_RATE = 5;   // ₹5 per extra blouse / extra saree in a combo

const BLOUSE_FREE_QTY  = 4;    // blouses only: ₹79 covers up to 4
const BLOUSE_EXTRA_RATE = 5;   // blouses only: +₹5 for each blouse after that
const BLOUSE_BASE_RATE  = 79;  // ✅ flat pan-India rate for
                                // blouses only; no longer varies by
                                // state like every other category
const SAREE_FREE_QTY   = 1; // base rate covers only the 1st saree

const calculateShipping = (items, stateName) => {
  if (!items || items.length === 0) return 0;
  const BASE = getStateShippingRate(stateName);
  const SAREE_EXTRA_RATE = BASE / 2; // ₹40 (AP/TS) or ₹50 (others) per extra saree

  let blouseQty = 0, sareeQty = 0, otherQty = 0;
  items.forEach(item => {
    const qty = item.quantity || 1;
    if      (isBlouse(item))                      blouseQty += qty;
    else if (isSaree(item) || isMulCotton(item))  sareeQty  += qty;
    else                                          otherQty  += qty;
  });

  // ✅ COMBO SHIPPING — sarees / mul cotton with or without blouses (and
  // nothing else in the cart):
  //   • the first saree costs the state rate (₹80 AP & Telangana, ₹100 others)
  //     and ONE blouse ships free with it
  //   • every extra blouse  → +₹5
  //   • every extra saree   → +₹5
  if (sareeQty >= 1 && otherQty === 0) {
    const extraSarees  = sareeQty - 1;
    const extraBlouses = Math.max(0, blouseQty - 1);
    return BASE + (extraSarees + extraBlouses) * COMBO_EXTRA_RATE;
  }

  // ✅ Blouses now use the flat BLOUSE_BASE_RATE (₹80, pan-India)
  // instead of the state-dependent BASE — sarees and "other" below
  // are completely untouched, still state-based as before.
  const blouseCharge = blouseQty > 0
    ? BLOUSE_BASE_RATE + Math.max(0, blouseQty - BLOUSE_FREE_QTY) * BLOUSE_EXTRA_RATE : 0;
  const sareeCharge = sareeQty > 0
    ? BASE + Math.max(0, sareeQty - SAREE_FREE_QTY) * SAREE_EXTRA_RATE : 0;
  const otherCharge = otherQty > 0 ? BASE : 0;

  const active = [blouseCharge, sareeCharge, otherCharge].filter(c => c > 0);
  return active.length > 0 ? Math.max(...active) : 0;
};

// ══════════════════════════════════════════════════════
//  ✅ NEW — SAVED ADDRESS BOOK (stored in this browser)
//  `savedAddresses` holds every address the customer saved.
//  `userData` is still kept in sync with the SELECTED address,
//  because the cart / payment pages read the delivery state from it.
// ══════════════════════════════════════════════════════
const ADDR_KEY    = "savedAddresses";
const SEL_KEY     = "selectedAddressId";
const ADDR_FIELDS = ["fullName","phone","houseNo","landmark","village","district","state","pincode"];

const readJSON = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v ?? fallback; }
  catch { return fallback; }
};
const emptyAddr = () => ({
  fullName: localStorage.getItem("userName") || "",
  phone:"", houseNo:"", landmark:"", village:"", district:"", state:"", pincode:"",
});
const pickFields = (a) =>
  ADDR_FIELDS.reduce((o, k) => ({ ...o, [k]: (a && a[k]) || "" }), {});

// the pincode is the source of truth for the state (same rule as before)
const withDetectedState = (a) => {
  const d = a.pincode && a.pincode.length === 6 ? detectStateFromPincode(a.pincode) : null;
  return d ? { ...a, state: d } : a;
};
const syncUserData = (fields) =>
  localStorage.setItem("userData", JSON.stringify({ ...readJSON("userData", {}), ...fields }));

function loadAddressBook() {
  const userData = readJSON("userData", {});
  let list = readJSON(ADDR_KEY, []);
  if (!Array.isArray(list)) list = [];

  // first visit after this update: the single address saved earlier
  // becomes the first entry of the address book
  if (list.length === 0 && userData.houseNo && userData.state) {
    list = [{ id: String(Date.now()), ...pickFields(userData) }];
    localStorage.setItem(ADDR_KEY, JSON.stringify(list));
  }

  const selId    = localStorage.getItem(SEL_KEY);
  const selected = list.find(a => a.id === selId) || list[0] || null;
  if (selected) {
    localStorage.setItem(SEL_KEY, selected.id);
    syncUserData(withDetectedState(pickFields(selected)));
  }
  return { list, selected };
}

// ══════════════════════════════════════════════════════
//  ✅ NEW — STATE DROPDOWN WITH SEARCH
//  Type to filter ("tel" → Telangana), tap / click or use ↑ ↓ Enter.
// ══════════════════════════════════════════════════════
function StateSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [q,    setQ]    = useState("");
  const [hi,   setHi]   = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQ(""); }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, []);

  const term     = q.trim().toLowerCase();
  const filtered = term ? options.filter(s => s.toLowerCase().includes(term)) : options;

  const choose = (s) => { onChange(s); setOpen(false); setQ(""); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { if (open && filtered[hi]) { e.preventDefault(); choose(filtered[hi]); } }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div className="ck2-state" ref={wrapRef}>
      <input type="text" className="ck-input" autoComplete="off"
        value={open ? q : value}
        placeholder={open && value ? value : "Search or select state"}
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onKeyDown={onKey}/>
      <span className="ck2-state-caret" aria-hidden="true">▾</span>
      {open && (
        <div className="ck2-state-list" role="listbox">
          {filtered.length === 0
            ? <div className="ck2-state-none">No state found</div>
            : filtered.map((s, i) => (
                <div key={s} role="option" aria-selected={s === value}
                  className={`ck2-state-opt${i === hi ? " hi" : ""}${s === value ? " sel" : ""}`}
                  onMouseDown={e => { e.preventDefault(); choose(s); }}
                  onMouseEnter={() => setHi(i)}>
                  {s}
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

export default function Checkout({ cartItems, setCartItems }) {
  const navigate = useNavigate();

  // ✅ address book — `address` is the SELECTED delivery address (used for
  // shipping + the order); `form` is only what's being typed in the form.
  const [book] = useState(loadAddressBook);
  const [addresses,  setAddresses]  = useState(book.list);
  const [selectedId, setSelectedId] = useState(book.selected ? book.selected.id : null);
  const [address,    setAddress]    = useState(() =>
    book.selected ? withDetectedState(pickFields(book.selected)) : emptyAddr()
  );
  const [addressSaved, setAddressSaved] = useState(!!book.selected);

  // "view" = selected address, "pick" = choose / add another, "form" = add or edit
  const [mode,      setMode]      = useState("view");
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(emptyAddr);
  const [formError, setFormError] = useState("");

  // ✅ NEW — tracks whether the state was auto-filled from pincode
  // or manually typed, so we don't clobber a manual edit
  const [stateAutoFilled, setStateAutoFilled] = useState(false);

  // ✅ Real-time stock validation at checkout
  const [stockErrors,  setStockErrors]  = useState([]);
  const [validating,   setValidating]   = useState(false);

  useEffect(() => {
    validateAllStock();
  }, []); // eslint-disable-line

  // Auto-fill the State from a full 6-digit pincode while the form is open.
  // The pincode is the source of truth for shipping; the dropdown can still
  // be changed by hand and is re-synced the next time the pincode changes.
  useEffect(() => {
    if (mode !== "form") return;
    if (form.pincode.length === 6) {
      const detected = detectStateFromPincode(form.pincode);
      if (detected) {
        setForm(prev => ({ ...prev, state: detected }));
        setStateAutoFilled(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pincode]);

  const validateAllStock = async () => {
    if (!cartItems || cartItems.length === 0) return;
    setValidating(true);
    const errors = [];

    for (const item of cartItems) {
      try {
        const res  = await fetch(`${API}/api/products/check-stock`, {
          method:  "POST",
          headers: { "Content-Type":"application/json" },
          body:    JSON.stringify({
            productId: item._id || item.id,
            size:      item.selectedSize || "",
            quantity:  item.quantity || 1,
          }),
        });
        const data = await res.json();
        if (!data.canAdd) {
          errors.push({
            name:  item.name,
            size:  item.selectedSize || "",
            avail: data.available,
            requested: item.quantity || 1,
          });
        }
      } catch { /* fail silently — let backend catch it */ }
    }

    setStockErrors(errors);
    setValidating(false);

    if (errors.length > 0) {
      const fixed = cartItems.map(item => {
        const err = errors.find(e =>
          e.name === item.name && e.size === (item.selectedSize || "")
        );
        if (err && err.avail === 0) return null;
        if (err && err.avail  >  0) return { ...item, quantity: err.avail };
        return item;
      }).filter(Boolean);
      setCartItems(fixed);
    }
  };

  const removeItem = (id, size) =>
    setCartItems(cartItems.filter(item =>
      !((item._id||item.id)===id && item.selectedSize===size)
    ));

  const updateQty = (itemId, size, delta) => {
    setCartItems(cartItems.map(item => {
      if ((item._id||item.id)===itemId && item.selectedSize===size) {
        const newQty     = (item.quantity||1) + delta;
        const sizeStock  = getStockForSize(item, size);
        if (newQty < 1)         return null;
        if (newQty > sizeStock) return item;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const getImage = (item) => {
    const img = item.images?.[0] || item.image || "";
    if (!img) return "/images/s1.png";
    if (img.startsWith("http"))     return img;
    if (img.startsWith("uploads/")) return `${API}/${img}`;
    return "/images/s1.png";
  };

  // ✅ Shipping now computed from the (pincode-detected) address state,
  // using the unified blouse/saree/other logic
  // ✅ Delivery charges depend on the state of the delivery address, so they only
  // exist once an address has been entered — before that the total is items only.
  const shipping   = addressSaved ? calculateShipping(cartItems, address.state) : 0;
  const isAPTS     = AP_TS_STATES.some(s => s.toLowerCase() === (address.state||"").toLowerCase());

  const subtotal   = cartItems.reduce((s, i) => s + Number(i.price)*(i.quantity||1), 0);
  const savings    = cartItems.reduce((s, i) =>
    s + ((Number(i.originalPrice||i.price)-Number(i.price))*(i.quantity||1)), 0);
  const finalTotal = subtotal + shipping;

  // make one saved address the delivery address
  const applyAddress = (a) => {
    const fields = withDetectedState(pickFields(a));
    setAddress(fields);
    setSelectedId(a.id);
    localStorage.setItem(SEL_KEY, a.id);
    syncUserData(fields);          // cart / payment pages read this
    setAddressSaved(true);
  };

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyAddr());
    setStateAutoFilled(false);
    setFormError("");
    setMode("form");
  };

  const openEditForm = (a) => {
    setEditingId(a.id);
    setForm(pickFields(a));
    setStateAutoFilled(false);
    setFormError("");
    setMode("form");
  };

  const cancelForm = () => {
    setFormError("");
    setEditingId(null);
    setMode(addresses.length > 0 ? "pick" : "view");
  };

  const handleSaveAddress = () => {
    const required = ["fullName","phone","houseNo","village","district","state","pincode"];
    const missing  = required.find(f => !form[f]?.trim());
    if (missing)                     { setFormError("Please fill all required fields."); return; }
    if (form.phone.length < 10)      { setFormError("Enter a valid 10-digit phone number."); return; }
    if (form.pincode.length !== 6)   { setFormError("Enter a valid 6-digit pincode."); return; }

    const clean = ADDR_FIELDS.reduce((o, k) => ({ ...o, [k]: (form[k] || "").trim() }), {});
    let saved, list;
    if (editingId) {
      saved = { ...clean, id: editingId };
      list  = addresses.map(a => (a.id === editingId ? saved : a));
    } else {
      saved = { ...clean, id: String(Date.now()) };
      list  = [...addresses, saved];
    }
    localStorage.setItem(ADDR_KEY, JSON.stringify(list));
    setAddresses(list);
    applyAddress(saved);           // the address just saved becomes the delivery address
    setEditingId(null);
    setFormError("");
    setMode("view");
  };

  const removeAddress = (id) => {
    if (!window.confirm("Remove this address?")) return;
    const list = addresses.filter(a => a.id !== id);
    localStorage.setItem(ADDR_KEY, JSON.stringify(list));
    setAddresses(list);
    if (id !== selectedId) return;

    if (list.length > 0) {
      applyAddress(list[0]);
    } else {
      localStorage.removeItem(SEL_KEY);
      syncUserData(pickFields({}));
      setSelectedId(null);
      setAddress(emptyAddr());
      setAddressSaved(false);
      setMode("view");
    }
  };

  if (cartItems.length === 0) return (
    <div className="ck-empty">
      <div className="ck-empty-icon">🛍️</div>
      <h2>Your cart is empty</h2>
      <p>Looks like you haven't added anything yet</p>
      <button className="ck-empty-btn" onClick={() => navigate("/home")}>
        Browse Sarees
      </button>
    </div>
  );

  return (
    <div className="ck-root">

      {/* PROGRESS */}
      <div className="ck-steps">
        {[{num:1,label:"Cart"},{num:2,label:"Address"},{num:3,label:"Payment"}].map((s,i,arr) => (
          <React.Fragment key={s.num}>
            <div className={`ck-step ${s.num<=2?"active":""}`}>
              <div className="ck-step-circle">
                {s.num<2?<FaCheckCircle/>:s.num}
              </div>
              <span className="ck-step-label">{s.label}</span>
            </div>
            {i<arr.length-1 && <div className={`ck-step-line ${s.num<2?"done":""}`}/>}
          </React.Fragment>
        ))}
      </div>

      {validating && (
        <div style={{ background:"#fff8e1",border:"1px solid #ffc107",
          borderRadius:8,padding:"10px 16px",marginBottom:16,
          fontSize:13,color:"#856404" }}>
          🔍 Verifying stock availability…
        </div>
      )}
      {stockErrors.length > 0 && (
        <div style={{ background:"#fff5f5",border:"1px solid #fecaca",
          borderRadius:8,padding:"12px 16px",marginBottom:16 }}>
          <div style={{ fontWeight:700,color:"#b91c1c",fontSize:13,marginBottom:6 }}>
            ⚠️ Stock has changed for some items:
          </div>
          {stockErrors.map((e,i) => (
            <div key={i} style={{ fontSize:12,color:"#b91c1c",marginBottom:3 }}>
              • <strong>{e.name}{e.size?` (Size: ${e.size})`:""}</strong>:{" "}
              {e.avail===0?"now sold out":`only ${e.avail} left (quantities adjusted)`}
            </div>
          ))}
        </div>
      )}

      <div className="ck-body">

        {/* LEFT */}
        <div className="ck-left">

          {/* ADDRESS CARD */}
          <div className="ck-card">
            <style>{`
              .ck2-addr-list { display:flex; flex-direction:column; gap:10px; padding:14px 18px 6px; }
              .ck2-addr {
                display:flex; gap:12px; align-items:flex-start; cursor:pointer;
                background:#fff; border:1.5px solid #e8dccb; border-radius:12px;
                padding:14px; transition:border-color .15s, box-shadow .15s, background .15s;
              }
              .ck2-addr:hover { border-color:#c9853a; }
              .ck2-addr.sel { border-color:#c9853a; background:#fffaf2; box-shadow:0 0 0 3px rgba(201,133,58,.12); }
              .ck2-radio {
                width:18px; height:18px; border-radius:50%; flex-shrink:0; margin-top:3px;
                border:2px solid #c9b79c; position:relative; background:#fff;
              }
              .ck2-addr.sel .ck2-radio { border-color:#c9853a; }
              .ck2-addr.sel .ck2-radio::after {
                content:""; position:absolute; inset:2px; border-radius:50%; background:#c9853a;
              }
              .ck2-addr-name { font-weight:700; color:#2c1810; font-size:15px; display:flex; flex-wrap:wrap; gap:4px 10px; align-items:baseline; }
              .ck2-addr-phone { font-weight:500; color:#6b5646; font-size:13px; }
              .ck2-addr-line { color:#4a3a2e; font-size:14px; line-height:1.5; margin-top:2px; }
              .ck2-addr-actions { display:flex; gap:16px; margin-top:8px; }
              .ck2-link {
                background:none; border:none; padding:0; cursor:pointer;
                font-size:12px; font-weight:700; color:#c9853a; text-decoration:underline;
              }
              .ck2-link.danger { color:#c62828; }
              .ck2-add-new {
                display:block; width:calc(100% - 36px); margin:8px 18px 18px;
                background:#fffaf2; border:1.5px dashed #c9853a; color:#c9853a;
                border-radius:12px; padding:13px; font-size:14px; font-weight:700; cursor:pointer;
              }
              .ck2-add-new:hover { background:#fff3e0; }
              .ck2-state { position:relative; }
              .ck2-state-caret { position:absolute; right:14px; top:50%; transform:translateY(-50%); color:#8a6a50; pointer-events:none; font-size:12px; }
              .ck2-state .ck-input { padding-right:34px; }
              .ck2-state-list {
                margin-top:6px; max-height:200px; overflow-y:auto; background:#fff;
                border:1.5px solid #e8dccb; border-radius:10px;
              }
              .ck2-state-opt { padding:11px 14px; font-size:14px; color:#2c1810; cursor:pointer; }
              .ck2-state-opt.hi  { background:#fdf3e4; }
              .ck2-state-opt.sel { font-weight:700; color:#c9853a; }
              .ck2-state-none { padding:12px 14px; font-size:13px; color:#8a6a50; }
            `}</style>

            <div className="ck-card-head">
              <div className="ck-card-title">
                <FaMapMarkerAlt className="ck-card-icon"/>
                <span>Delivery Address</span>
              </div>
              {addressSaved && mode === "view" && (
                <button className="ck-link-btn"
                  onClick={() => { setMode("pick"); setFormError(""); }}>
                  <FaEdit style={{fontSize:11,marginRight:4}}/>Change
                </button>
              )}
              {mode === "pick" && (
                <button className="ck-link-btn" onClick={() => setMode("view")}>Cancel</button>
              )}
              {mode === "form" && (
                <button className="ck-link-btn" onClick={cancelForm}>Cancel</button>
              )}
            </div>

            {/* SELECTED ADDRESS */}
            {addressSaved && mode === "view" && (
              <div className="ck-address-display">
                <div className="ck-address-name">
                  {address.fullName}
                  <span className="ck-address-phone">{address.phone}</span>
                </div>
                <div className="ck-address-line">
                  {address.houseNo}{address.landmark?`, ${address.landmark}`:""},{" "}
                  {address.village}, {address.district}
                </div>
                <div className="ck-address-line">
                  {address.state} — <strong>{address.pincode}</strong>
                </div>

                {/* ✅ Shipping zone badge stays hidden per the earlier request —
                    isAPTS is untouched and still drives the shipping calculation. */}

                <div className="ck-address-tag" style={{ marginTop:8 }}>
                  <FaCheckCircle style={{ color:"#2e7d32",fontSize:12 }}/>
                  Delivery available
                </div>
              </div>
            )}

            {!addressSaved && mode === "view" && (
              <div className="ck-address-empty">
                <FaMapMarkerAlt style={{ fontSize:28,color:"#c9a24a",marginBottom:8 }}/>
                <p>Add a delivery address to continue</p>
                <button className="ck-add-addr-btn" onClick={openNewForm}>
                  + Add New Address
                </button>
              </div>
            )}

            {/* ✅ CHANGE → choose a saved address, or add another */}
            {mode === "pick" && (
              <>
                <div className="ck2-addr-list">
                  {addresses.map(a => (
                    <div key={a.id}
                      className={`ck2-addr${a.id === selectedId ? " sel" : ""}`}
                      onClick={() => { applyAddress(a); setMode("view"); }}>
                      <span className="ck2-radio"/>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="ck2-addr-name">
                          {a.fullName}<span className="ck2-addr-phone">{a.phone}</span>
                        </div>
                        <div className="ck2-addr-line">
                          {a.houseNo}{a.landmark?`, ${a.landmark}`:""}, {a.village}, {a.district}
                        </div>
                        <div className="ck2-addr-line">
                          {a.state} — <strong>{a.pincode}</strong>
                        </div>
                        <div className="ck2-addr-actions">
                          <button className="ck2-link"
                            onClick={e => { e.stopPropagation(); openEditForm(a); }}>Edit</button>
                          <button className="ck2-link danger"
                            onClick={e => { e.stopPropagation(); removeAddress(a.id); }}>Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="ck2-add-new" onClick={openNewForm}>
                  + Add a new address
                </button>
              </>
            )}

            {/* ADD / EDIT FORM */}
            {mode === "form" && (
              <div className="ck-form">
                {formError && <div className="ck-form-error">{formError}</div>}
                <div className="ck-form-grid">
                  {[
                    { key:"fullName", label:"Full Name",            type:"text", req:true  },
                    { key:"phone",    label:"Phone",                type:"tel",  req:true  },
                    { key:"houseNo",  label:"House / Flat No",      type:"text", req:true  },
                    { key:"landmark", label:"Landmark (optional)",  type:"text", req:false },
                    { key:"village",  label:"Village / City",       type:"text", req:true  },
                    { key:"district", label:"District",             type:"text", req:true  },
                  ].map(({ key, label, type, req }) => (
                    <div className="ck-field" key={key}>
                      <label className="ck-label">{label}{req&&" *"}</label>
                      <input type={type} value={form[key]}
                        onChange={e => setForm({...form,[key]:e.target.value})}
                        className="ck-input"
                        placeholder={`Enter ${label.toLowerCase()}`}/>
                    </div>
                  ))}

                  {/* Pincode — auto-fills State below */}
                  <div className="ck-field">
                    <label className="ck-label">
                      Pincode *
                      {form.pincode.length === 6 && detectStateFromPincode(form.pincode) && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, fontWeight: 700,
                          color: AP_TS_STATES.includes(detectStateFromPincode(form.pincode)) ? "#2e7d32" : "#c9a24a",
                          background: AP_TS_STATES.includes(detectStateFromPincode(form.pincode)) ? "#e8f5e9" : "#fff8e1",
                          padding: "1px 7px", borderRadius: 10,
                        }}>
                          {detectStateFromPincode(form.pincode)} detected
                        </span>
                      )}
                    </label>
                    <input type="text" value={form.pincode}
                      maxLength={6}
                      onChange={e => setForm({...form, pincode: e.target.value.replace(/\D/g,"")})}
                      className="ck-input"
                      placeholder="6-digit pincode"/>
                  </div>

                  {/* ✅ State — searchable dropdown, auto-filled from pincode */}
                  <div className="ck-field">
                    <label className="ck-label">
                      State *
                      <span style={{ marginLeft:8, fontSize:10, color:"#8a4a30", fontWeight:400 }}>
                        (auto-filled from pincode)
                      </span>
                    </label>
                    <StateSelect
                      value={form.state}
                      options={INDIA_STATES}
                      onChange={v => { setForm({...form, state: v}); setStateAutoFilled(false); }}/>
                  </div>
                </div>

                <button className="ck-save-addr-btn" onClick={handleSaveAddress}>
                  Save & Continue
                </button>
              </div>
            )}
          </div>

          {/* ITEMS */}
          {addressSaved && mode === "view" && (
            <div className="ck-card">
              <div className="ck-card-head">
                <div className="ck-card-title">
                  <span>Order Items ({cartItems.length})</span>
                </div>
              </div>

              {cartItems.map(item => {
                const id         = item._id || item.id;
                const size       = item.selectedSize || "";
                const qty        = item.quantity || 1;
                const sizeStock  = getStockForSize(item, size);
                const atMax      = qty >= sizeStock;
                const itemTotal  = Number(item.price) * qty;

                return (
                  <div className="ck-item" key={`${id}_${size}`}>
                    <div className="ck-item-img-wrap">
                      <img src={getImage(item)} alt={item.name}
                        className="ck-item-img"
                        onError={e=>{e.target.src="/images/s1.png";}}/>
                    </div>
                    <div className="ck-item-info">
                      <div className="ck-item-name">{item.name}</div>
                      {item.fabric && (
                        <div className="ck-item-type">
                          {item.fabric}{item.color?` · ${item.color}`:""}
                        </div>
                      )}
                      {size && (
                        <div className="ck-item-meta">
                          Size: <strong>{size}</strong>
                          <span style={{ marginLeft:8,fontSize:11,
                            color:sizeStock<=3?"#c62828":"#8a4a30",fontWeight:600 }}>
                            ({sizeStock} left in this size)
                          </span>
                        </div>
                      )}
                      <div className="ck-item-meta">Qty: <strong>{qty}</strong></div>
                      <div className="ck-item-price-row">
                        <span className="ck-item-price">₹{itemTotal.toLocaleString()}</span>
                        {item.originalPrice > item.price && (
                          <span className="ck-item-old">
                            ₹{(item.originalPrice*qty).toLocaleString()}
                          </span>
                        )}
                      </div>

                      <div style={{ display:"flex",alignItems:"center",gap:8,marginTop:8 }}>
                        <button onClick={() => updateQty(id,size,-1)} disabled={qty<=1}
                          style={{ width:26,height:26,border:"1px solid #ecd9bd",
                            background:qty<=1?"#f5f5f5":"#fff",
                            cursor:qty<=1?"not-allowed":"pointer",
                            fontSize:16,color:"#5c2e1f",fontWeight:700,
                            borderRadius:3,display:"flex",alignItems:"center",
                            justifyContent:"center",opacity:qty<=1?0.4:1 }}>−</button>
                        <span style={{ fontSize:14,fontWeight:700,color:"#5c2e1f",
                          minWidth:20,textAlign:"center" }}>{qty}</span>
                        <button onClick={() => updateQty(id,size,1)} disabled={atMax}
                          style={{ width:26,height:26,border:"1px solid #ecd9bd",
                            background:atMax?"#f5f5f5":"#fff",
                            cursor:atMax?"not-allowed":"pointer",
                            fontSize:16,color:"#5c2e1f",fontWeight:700,
                            borderRadius:3,display:"flex",alignItems:"center",
                            justifyContent:"center",opacity:atMax?0.4:1 }}>+</button>
                        {atMax && (
                          <span style={{ fontSize:10,color:"#c62828",fontWeight:600 }}>
                            Max {sizeStock}{size?` (${size})`:""}
                          </span>
                        )}
                      </div>

                      <div className="ck-item-delivery">
                        <FaTruck style={{ fontSize:12 }}/>
                        <span className="ck-free-ship">Standard Delivery</span>
                      </div>
                    </div>
                    <button className="ck-remove-btn"
                      onClick={() => removeItem(id, size)} title="Remove">
                      <FaTrash/>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="ck-right">

          <div className="ck-card ck-coupon-card">
            <div className="ck-coupon-row">
              <FaTag style={{ color:"#c9a24a",fontSize:14 }}/>
              <span className="ck-coupon-text">Have a coupon code?</span>
              <button className="ck-link-btn">Apply</button>
            </div>
          </div>

          <div className="ck-card ck-price-card">
            <div className="ck-price-head">Price Details</div>
            <div className="ck-price-row">
              <span>Price ({cartItems.length} item{cartItems.length>1?"s":""})</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            {savings > 0 && (
              <div className="ck-price-row ck-discount">
                <span>Discount</span>
                <span>− ₹{savings.toLocaleString()}</span>
              </div>
            )}
            <div className="ck-price-row">
              <span>Delivery Charges</span>
              {addressSaved ? (
                <span style={{ color:isAPTS?"#2e7d32":"#5c2e1f",fontWeight:600 }}>
                  ₹{shipping}
                </span>
              ) : (
                <span style={{ color:"#8a6a50",fontSize:12,textAlign:"right" }}>
                  Applicable based on your locality
                </span>
              )}
            </div>
            {/* ✅ Zone-rate badge hidden per request — no breakdown of
                which rate zone applies shown to the customer. isAPTS
                still used correctly elsewhere for the actual delivery
                charge computation above; only this explanatory label
                is removed.
            <div style={{ fontSize:11,color:isAPTS?"#2e7d32":"#8a4a30",
              marginBottom:10,padding:"4px 0" }}>
              {isAPTS?"🟢 AP & Telangana rate":"🟡 Standard delivery rate"}
            </div>
            */}
            <div className="ck-price-divider"/>
            <div className="ck-price-row ck-total">
              <span>Total Amount</span>
              <span>₹{finalTotal.toLocaleString()}</span>
            </div>
            {savings > 0 && (
              <div className="ck-save-banner">
                🎉 You save <strong>₹{savings.toLocaleString()}</strong> on this order
              </div>
            )}
          </div>

          {!addressSaved && (
            <div className="ck-addr-warn">
              ⚠ Please add a delivery address to proceed
            </div>
          )}

          {stockErrors.length > 0 && (
            <div style={{ background:"#fff5f5",border:"1px solid #fecaca",
              borderRadius:6,padding:"10px 14px",marginBottom:12,
              fontSize:12,color:"#b91c1c",fontWeight:600 }}>
              ⚠️ Please review stock issues above before proceeding
            </div>
          )}

          {/* ✅ PLACE ORDER — passes address + computed shipping/state through to Payment */}
          <button
            className="ck-place-btn"
            disabled={!addressSaved || stockErrors.length > 0 || validating}
            onClick={() => navigate("/payment", {
              state: {
                cartItems,
                finalTotal,
                address,
                shipping,
                shippingState: address.state,
              }
            })}>
            <FaLock style={{ fontSize:13 }}/>
            Place Order · ₹{finalTotal.toLocaleString()}
          </button>

          <div className="ck-secure">
            <FaShieldAlt style={{ color:"#2e7d32",fontSize:13 }}/>
            <span>Safe & Secure Payments.</span>
          </div>

          <div className="ck-pay-icons">
            {["UPI","Cards","Net Banking",].map(p => (
              <span key={p} className="ck-pay-pill">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}